import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Moon, Wind, Pause, Play } from "lucide-react";

interface FourSevenEightBreathingProps {
  onClose: () => void;
}

type Phase = "inhale" | "hold" | "exhale" | "pause";

const phaseDurations = {
  inhale: 4,
  hold: 7,
  exhale: 8,
  pause: 0,
};

const phaseText = {
  inhale: "Breathe In",
  hold: "Hold",
  exhale: "Breathe Out Slowly",
  pause: "Relax",
};

const phaseColors = {
  inhale: "from-blue-400 to-indigo-400",
  hold: "from-purple-400 to-pink-400",
  exhale: "from-indigo-400 to-blue-400",
  pause: "from-gray-300 to-gray-400",
};

export const FourSevenEightBreathing = ({ onClose }: FourSevenEightBreathingProps) => {
  const [phase, setPhase] = useState<Phase>("pause");
  const [countdown, setCountdown] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [totalCycles] = useState(8);

  useEffect(() => {
    if (!isActive || phase === "pause" || isPaused) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          let nextPhase: Phase;
          if (phase === "inhale") {
            nextPhase = "hold";
          } else if (phase === "hold") {
            nextPhase = "exhale";
          } else {
            setCycleCount((c) => c + 1);
            nextPhase = "pause";
            setTimeout(() => {
              if (cycleCount + 1 < totalCycles) {
                setPhase("inhale");
                setCountdown(phaseDurations.inhale);
              }
            }, 2000);
          }
          
          if (nextPhase !== "pause") {
            setPhase(nextPhase);
            return phaseDurations[nextPhase];
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, isActive, isPaused, cycleCount, totalCycles]);

  const start = () => {
    setIsActive(true);
    setIsPaused(false);
    setPhase("inhale");
    setCountdown(phaseDurations.inhale);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const reset = () => {
    // Soft reset: keep the active view, restart from the beginning paused on inhale
    setCycleCount(0);
    setPhase("inhale");
    setCountdown(phaseDurations.inhale);
    setIsActive(true);
    setIsPaused(true);
  };

  const progress = (cycleCount / totalCycles) * 100;
  const isComplete = cycleCount >= totalCycles;

  const getCircleScale = () => {
    if (phase === "pause") return 0.5;
    const elapsed = phaseDurations[phase] - countdown;
    const duration = phaseDurations[phase];
    const phaseProgress = elapsed / duration;

    if (phase === "inhale") {
      return 0.5 + (phaseProgress * 0.5);
    } else if (phase === "exhale") {
      return 1 - (phaseProgress * 0.5);
    }
    return 1;
  };

  if (isComplete) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-12 text-center relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4"
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 flex items-center justify-center">
              <Moon className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4 text-foreground">Feeling Drowsy?</h2>
            <p className="text-lg text-muted-foreground mb-2">
              You completed {cycleCount} breathing cycles
            </p>
            <p className="text-muted-foreground">
              Your body should feel relaxed and ready for sleep. Head to bed when you feel drowsy.
            </p>
          </div>

          <div className="flex gap-3 justify-center">
            <Button onClick={reset}>
              Do More Cycles
            </Button>
            <Button variant="outline" onClick={onClose}>
              Ready for Sleep
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Button
          variant="ghost"
          onClick={onClose}
          className="mb-6 gap-2"
        >
          <X className="h-4 w-4" />
          Exit Exercise
        </Button>

        <Card className="p-8 md:p-12">
          {!isActive ? (
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4 text-foreground">4-7-8 Sleep Breathing</h2>
              <p className="text-lg text-muted-foreground mb-8">
                A powerful breathing technique that helps you fall asleep in minutes by activating your relaxation response.
              </p>

              <div className="bg-indigo-50 dark:bg-indigo-950 rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold mb-3 text-foreground">The Pattern:</h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>• Inhale through nose for <strong>4 seconds</strong></p>
                  <p>• Hold breath for <strong>7 seconds</strong></p>
                  <p>• Exhale through mouth for <strong>8 seconds</strong></p>
                  <p className="text-sm mt-4">Repeat 8 cycles for best results</p>
                </div>
              </div>

              <Button size="lg" onClick={start} className="gap-2">
                <Moon className="h-5 w-5" />
                Begin Breathing Exercise
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>Cycle {cycleCount + 1} of {totalCycles}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="flex justify-center mb-8">
                <div className="relative w-80 h-80 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-4 border-indigo-200 dark:border-indigo-800" />
                  <div className="absolute inset-4 rounded-full border-2 border-indigo-100 dark:border-indigo-900" />
                  
                  <div 
                    className={`absolute rounded-full bg-gradient-to-r ${phaseColors[phase]} transition-all duration-1000 ease-in-out flex items-center justify-center`}
                    style={{
                      width: `${getCircleScale() * 100}%`,
                      height: `${getCircleScale() * 100}%`,
                      boxShadow: `0 0 ${getCircleScale() * 60}px ${getCircleScale() * 20}px rgba(99, 102, 241, 0.3)`
                    }}
                  >
                    <div className="text-center text-white">
                      {countdown > 0 && (
                        <div className="text-6xl font-bold mb-2">{countdown}</div>
                      )}
                      <div className="text-xl font-medium opacity-90">{phaseText[phase]}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center space-y-3">
                <div className="flex gap-3 justify-center">
                  <Button 
                    type="button"
                    onClick={togglePause}
                    size="lg"
                    className="gap-2 min-w-[140px]"
                  >
                    {isPaused ? (
                      <>
                        <Play className="h-5 w-5" />
                        Resume
                      </>
                    ) : (
                      <>
                        <Pause className="h-5 w-5" />
                        Pause
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={reset}
                  >
                    Reset
                  </Button>
                </div>
                {isPaused && (
                  <p className="text-sm text-muted-foreground animate-fade-in">
                    Exercise paused - click Resume when ready
                  </p>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
